import { Play, Square, BellOff, Delete, ClipboardCheck, AlertTriangle, CheckCircle2 } from "lucide-react";
import { FLUID_TYPES } from "@/data/fluids";
import { PumpKey, ScoreRow } from "@/components/fluid/PumpControls";

const SPEEDS = [
  { v: 60, label: "60×" },
  { v: 120, label: "120×" },
  { v: 300, label: "300×" },
];

const fmtClock = (totalMin) => {
  const h = Math.floor(totalMin / 60);
  const m = Math.floor(totalMin % 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

// The IV pole + volumetric infusion pump (right column of the Fluid Pump trainer).
export function PumpDevice({
  bagLevel,
  fluid,
  running,
  dripDur,
  alarm,
  statusColor,
  status,
  field,
  rateNum,
  vtbiNum,
  timeNum,
  infused,
  pct,
  elapsedMin,
  remainingMin,
  score,
  speed,
  setField,
  pressDigit,
  delDigit,
  bump,
  start,
  stop,
  setAlarm,
  verify,
  clearAll,
  setSpeed,
}) {
  return (
    <div className="flex gap-4 items-stretch" data-testid="infusion-pump">
      {/* IV pole + animated drip bag + chamber (rate-driven) */}
      <div className="hidden sm:flex flex-col items-center w-24 shrink-0" data-testid="drip-bag">
        <div className="w-1.5 h-4 bg-slate-300 rounded-t" />
        {/* fluid bag — drains as the infusion is delivered */}
        <div className="relative w-16 h-28 rounded-b-xl rounded-t-md border-2 border-sky-300 bg-gradient-to-b from-white to-sky-50 overflow-hidden">
          <div
            className="absolute bottom-0 left-0 right-0 bg-sky-400/70 transition-[height] duration-300"
            style={{ height: `${bagLevel}%` }}
          />
          {[25, 50, 75].map((g) => (
            <span key={g} className="absolute right-0 w-2 h-px bg-sky-300/80" style={{ bottom: `${g}%` }} />
          ))}
          <span className="absolute inset-x-0 top-1.5 text-center text-[9px] font-mono text-sky-800 font-semibold">
            {FLUID_TYPES.find((f) => f.id === fluid)?.id}
          </span>
        </div>
        {/* neck into the drip chamber */}
        <div className="w-2 h-2 bg-slate-300" />
        {/* drip chamber — a drop forms and falls at the programmed flow rate */}
        <div
          className="relative w-12 h-20 rounded-b-xl rounded-t-md border-2 border-slate-300 bg-gradient-to-b from-white to-sky-50 overflow-hidden"
          data-testid="drip-chamber"
        >
          {running && (
            <span
              key={dripDur}
              className="drip-drop absolute left-1/2 -translate-x-1/2 top-1 w-2.5 h-3 rounded-full bg-sky-400"
              style={{ animationDuration: `${dripDur}s` }}
            />
          )}
          <div className="absolute bottom-0 left-0 right-0 h-4 bg-sky-300/70" />
        </div>
        {/* line down to the patient */}
        <div className="w-0.5 flex-1 min-h-[20px] bg-slate-300" />
        <div className="text-[9px] text-slate-400 mt-0.5">
          {running ? "infusing" : "stopped"}
        </div>
      </div>

      {/* pump device */}
      <div
        className={`flex-1 rounded-2xl bg-gradient-to-b from-slate-700 to-slate-800 p-4 shadow-2xl border-2 ${
          alarm ? "border-red-500 alarm-pulse" : "border-slate-900"
        }`}
      >
        {/* label + status LEDs */}
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] font-bold tracking-widest text-slate-300">
            VOLUMETRIC INFUSION PUMP
          </div>
          <div className="flex gap-2">
            <span
              className={`w-2.5 h-2.5 rounded-full ${
                running ? "bg-green-400 shadow-[0_0_8px_#4ade80]" : "bg-slate-600"
              }`}
              title="Run"
            />
            <span
              className={`w-2.5 h-2.5 rounded-full ${
                alarm ? "bg-red-500 shadow-[0_0_8px_#ef4444] animate-pulse" : "bg-slate-600"
              }`}
              title="Alarm"
            />
          </div>
        </div>

        {/* LCD */}
        <div className="rounded-lg bg-[#02110d] border border-emerald-900/60 p-4 font-mono">
          <div className="flex items-center justify-between mb-2">
            <span className={`text-[10px] font-bold tracking-widest ${statusColor}`} data-testid="pump-status">
              ● {status}
            </span>
            <span className="text-[10px] text-emerald-600">
              {FLUID_TYPES.find((f) => f.id === fluid)?.id}
            </span>
          </div>

          {/* RATE */}
          <button
            type="button"
            onClick={() => setField("rate")}
            data-testid="field-rate"
            className={`w-full text-left rounded px-2 py-1 mb-1 transition-colors ${
              field === "rate" ? "bg-emerald-900/40 ring-1 ring-emerald-500" : ""
            }`}
          >
            <div className="flex items-baseline justify-between">
              <span className="text-[10px] text-emerald-600 tracking-wider">RATE</span>
              <span className="text-[10px] text-emerald-600">mL/h</span>
            </div>
            <div className="text-3xl font-bold text-[#39FF14] tabular-nums leading-none" data-testid="display-rate">
              {rateNum.toFixed(0).padStart(3, "0")}
            </div>
          </button>

          {/* VTBI + TIME + INFUSED */}
          <div className="grid grid-cols-3 gap-1.5 mt-1">
            <button
              type="button"
              onClick={() => setField("vtbi")}
              data-testid="field-vtbi"
              className={`text-left rounded px-2 py-1 transition-colors ${
                field === "vtbi" ? "bg-emerald-900/40 ring-1 ring-emerald-500" : ""
              }`}
            >
              <div className="text-[10px] text-emerald-600 tracking-wider">VTBI mL</div>
              <div className="text-xl font-bold text-[#39FF14] tabular-nums" data-testid="display-vtbi">
                {vtbiNum.toFixed(0)}
              </div>
            </button>
            <button
              type="button"
              onClick={() => setField("time")}
              data-testid="field-time"
              className={`text-left rounded px-2 py-1 transition-colors ${
                field === "time" ? "bg-emerald-900/40 ring-1 ring-emerald-500" : ""
              }`}
            >
              <div className="text-[10px] text-emerald-600 tracking-wider">TIME h:m</div>
              <div className="text-xl font-bold text-[#39FF14] tabular-nums" data-testid="display-time">
                {fmtClock(timeNum)}
              </div>
            </button>
            <div className="px-2 py-1">
              <div className="text-[10px] text-emerald-600 tracking-wider">INFUSED</div>
              <div className="text-xl font-bold text-cyan-300 tabular-nums" data-testid="display-infused">
                {infused.toFixed(1)}
              </div>
            </div>
          </div>

          {/* progress + clocks */}
          <div className="mt-2 h-1.5 rounded-full bg-emerald-950 overflow-hidden">
            <div
              className="h-full bg-[#39FF14] transition-[width] duration-200"
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="flex justify-between text-[10px] text-emerald-600 mt-1">
            <span>ELAPSED {fmtClock(elapsedMin)}</span>
            <span>LEFT {fmtClock(remainingMin)}</span>
          </div>
        </div>

        {/* setting selector: switch between Flow rate, VTBI and Time */}
        <div className="grid grid-cols-3 gap-2 mt-3" data-testid="field-toggle">
          {[
            { f: "rate", label: "RATE", testid: "select-rate-btn" },
            { f: "vtbi", label: "VOLUME", testid: "select-vtbi-btn" },
            { f: "time", label: "TIME", testid: "select-time-btn" },
          ].map((b) => (
            <button
              key={b.f}
              type="button"
              onClick={() => setField(b.f)}
              data-testid={b.testid}
              className={`py-2 rounded-lg text-xs font-bold tracking-wide transition-colors ${
                field === b.f
                  ? "bg-sky-500 text-white"
                  : "bg-slate-600 text-slate-200 hover:bg-slate-500"
              }`}
            >
              {b.label}
            </button>
          ))}
        </div>
        <p className="text-center text-[10px] text-slate-400 mt-1.5">
          Editing{" "}
          <span className="text-sky-400 font-semibold">
            {field === "rate" ? "flow rate" : field === "vtbi" ? "volume (VTBI)" : "time"}
          </span>
          {" "}· volume = rate × time
        </p>

        {/* keypad */}
        <div className="grid grid-cols-3 gap-2 mt-2">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
            <PumpKey key={n} onClick={() => pressDigit(String(n))} testid={`key-${n}`}>
              {n}
            </PumpKey>
          ))}
          <PumpKey onClick={delDigit} testid="key-delete" className="text-amber-300">
            <Delete className="w-4 h-4 mx-auto" />
          </PumpKey>
          <PumpKey onClick={() => pressDigit("0")} testid="key-0">
            0
          </PumpKey>
          <div className="grid grid-rows-2 gap-1">
            <PumpKey
              onClick={() => bump(field === "rate" ? 1 : field === "vtbi" ? 10 : 15)}
              testid="key-up"
              small
            >
              ▲
            </PumpKey>
            <PumpKey
              onClick={() => bump(field === "rate" ? -1 : field === "vtbi" ? -10 : -15)}
              testid="key-down"
              small
            >
              ▼
            </PumpKey>
          </div>
        </div>

        {/* controls */}
        <div className="grid grid-cols-3 gap-2 mt-3">
          <button
            type="button"
            onClick={start}
            disabled={running || status === "COMPLETE"}
            data-testid="pump-start-btn"
            className="flex flex-col items-center gap-1 py-2.5 rounded-lg bg-green-600 hover:bg-green-500 disabled:opacity-40 text-white text-xs font-bold transition-colors"
          >
            <Play className="w-4 h-4" />
            START
          </button>
          <button
            type="button"
            onClick={stop}
            disabled={!running}
            data-testid="pump-stop-btn"
            className="flex flex-col items-center gap-1 py-2.5 rounded-lg bg-red-600 hover:bg-red-500 disabled:opacity-40 text-white text-xs font-bold transition-colors"
          >
            <Square className="w-4 h-4" />
            STOP
          </button>
          <button
            type="button"
            onClick={() => setAlarm(false)}
            data-testid="pump-silence-btn"
            className="flex flex-col items-center gap-1 py-2.5 rounded-lg bg-slate-600 hover:bg-slate-500 text-white text-xs font-bold transition-colors"
          >
            <BellOff className="w-4 h-4" />
            SILENCE
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-2">
          <button
            type="button"
            onClick={verify}
            data-testid="pump-verify-btn"
            className="flex items-center justify-center gap-1.5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-[11px] font-bold transition-colors"
          >
            <ClipboardCheck className="w-4 h-4" />
            VERIFY MY SETTINGS
          </button>
          <button
            type="button"
            onClick={clearAll}
            data-testid="pump-reset-btn"
            className="py-2 rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-700 text-[11px] font-semibold transition-colors"
          >
            CLEAR / RESET ALL
          </button>
        </div>

        {/* verify score panel */}
        {score && (
          <div
            data-testid="score-panel"
            className={`mt-3 rounded-lg border p-3 ${
              score.total >= 80
                ? "bg-emerald-500/10 border-emerald-500/40"
                : score.total >= 60
                ? "bg-amber-500/10 border-amber-500/40"
                : "bg-red-500/10 border-red-500/40"
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`text-3xl font-bold leading-none ${
                  score.total >= 80
                    ? "text-emerald-400"
                    : score.total >= 60
                    ? "text-amber-400"
                    : "text-red-400"
                }`}
                data-testid="score-grade"
              >
                {score.grade}
              </div>
              <div className="flex-1">
                <div className="text-sm font-bold text-slate-100" data-testid="score-total">
                  {score.total}/100
                </div>
                <div className="text-[10px] text-slate-400">
                  vs 2024 AAHA target for this order
                </div>
              </div>
            </div>
            <div className="mt-2 space-y-1 text-[11px]">
              <ScoreRow
                ok={score.rateOk}
                label="Flow rate"
                target={`${score.orderRate} mL/h`}
              />
              <ScoreRow
                ok={score.vtbiOk}
                label="Volume (VTBI)"
                target={`${score.orderVtbi} mL`}
              />
            </div>
          </div>
        )}

        {/* feedback strip */}
        {status === "COMPLETE" && (
          <div className="mt-3 flex items-center gap-2 bg-red-500/15 border border-red-500/40 rounded-md px-3 py-2 text-red-300 text-xs font-semibold" data-testid="complete-banner">
            <AlertTriangle className="w-4 h-4" />
            Infusion complete — {vtbiNum.toFixed(0)} mL delivered. Silence
            alarm &amp; reassess.
          </div>
        )}
        {status === "INFUSING" && (
          <div className="mt-3 flex items-center gap-2 bg-green-500/10 border border-green-500/30 rounded-md px-3 py-2 text-green-300 text-xs font-semibold">
            <CheckCircle2 className="w-4 h-4" />
            Infusing at {rateNum} mL/h · simulation {speed}×
          </div>
        )}

        {/* sim speed */}
        <div className="flex items-center justify-center gap-1.5 mt-3">
          <span className="text-[10px] text-slate-400 mr-1">SIM SPEED</span>
          {SPEEDS.map((s) => (
            <button
              key={s.v}
              type="button"
              onClick={() => setSpeed(s.v)}
              data-testid={`speed-${s.v}`}
              className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold transition-colors ${
                speed === s.v
                  ? "bg-sky-500 text-white"
                  : "bg-slate-700 text-slate-300 hover:bg-slate-600"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
